import { AsyncPipe } from '@angular/common';
import { Component, ChangeDetectionStrategy, Input, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ContactPersonModel } from '@models/contact-person.model';
import { LocalizedDatePipe } from '@modules/pipes/localized-date.pipe';
import { Option } from '@modules/ui/interfaces/option.interface';

import { ContactPersonCardComponent } from '../../card/contact-person-card/contact-person-card.component';
import { EventsService, Presenter } from '../../shared/services/events.service';
import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { SectionType } from '../enums/section-type.enum';

@Component({
  selector: 'mb-event-presenters-section',
  templateUrl: './event-presenter-section.component.html',
  styleUrls: ['./event-presenter-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropdownComponent, ContactPersonCardComponent, AsyncPipe, TranslatePipe],
})
export class EventPresentersSectionComponent implements OnInit {
  private eventsService = inject(EventsService);
  private localizedDatePipe = inject(LocalizedDatePipe);

  @Input() eventId!: string;

  @Output() showSection = new EventEmitter<boolean>();

  @Input() sectionType!: string;

  public presenters$ = new Observable<(ContactPersonModel & { availabilityPeriod: string })[]>();
  public filters$ = new Observable<Option[]>();
  public activeFilter$ = new BehaviorSubject<string>('');
  public collapseOnDesktop = false;

  private _presenters: Presenter[] = [];

  ngOnInit(): void {
    this.presenters$ = this.activeFilter$.pipe(
      switchMap((activeFilter: string) =>
        of(
          this._presenters
            .filter((presenter) => presenter.availabilities.includes(activeFilter))
            .map((presenter) => ({
              ...presenter.contact,
              availabilityPeriod: presenter.availabilityPeriod,
            }))
        )
      )
    );

    this.getUsers().subscribe((presenters: Presenter[]) => {
      this._presenters = presenters.sort((presenterA, presenterB) =>
        Intl.Collator().compare(presenterA.contact.lastname, presenterB.contact.lastname) === 0
          ? Intl.Collator().compare(presenterA.contact.firstname, presenterB.contact.firstname)
          : Intl.Collator().compare(presenterA.contact.lastname, presenterB.contact.lastname)
      );
      this.showSection.emit(!!presenters.length);

      // compute and sort filter options
      const availabilityDays = [...new Set(presenters.flatMap((e) => e.availabilities))].sort(
        (dateA, dateB) => +new Date(dateA) - +new Date(dateB)
      );
      this.filters$ = of([
        ...availabilityDays.map((day) => ({
          label: this.localizedDatePipe.transform(day, 'longDate'),
          value: day,
        })),
      ]);
      this.setFilter(availabilityDays[0]);

      // Collapse tabs to dropdown, if not enough space
      if (availabilityDays.length > 5) {
        this.collapseOnDesktop = true;
      }
    });
  }

  public setFilter(filter: string) {
    this.activeFilter$.next(filter);
  }

  private getUsers() {
    let presenterType: string = '';
    if (this.sectionType === SectionType.EVENT_EXPERTS) presenterType = 'experts';
    else if (this.sectionType === SectionType.EVENT_CONTACTS) presenterType = 'contacts';
    return this.eventsService.getCARLUsers(this.eventId, presenterType);
  }
}
